import { createRouter, createWebHistory, type RouteLocationGeneric } from 'vue-router'
import { useAuthStore, type User } from './stores/auth'
import { trackUi } from './audit'

type Role = User['role']
const keepQuery = (path: string) => (to: RouteLocationGeneric) => ({ path, query: to.query })

const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/change-password', component: () => import('./views/RequiredPasswordView.vue'), meta: { public: true, title: '请先修改密码' } },
    { path: '/login', component: () => import('./views/LoginView.vue'), meta: { public: true, title: '登录' } },
    { path: '/', redirect: '/dashboard' },
    { path: '/dashboard', component: () => import('./views/DashboardView.vue'), meta: { title: '今日工作台' } },
    { path: '/group-members', component: () => import('./views/GroupMembersView.vue'), meta: { title: '小组人员', roles: ['operator'] } },
    { path: '/group-products', redirect: keepQuery('/products/catalog') },
    { path: '/ranking', component: () => import('./views/GmvView.vue'), meta: { title: '业绩排行' } },
    { path: '/analytics/ranking', redirect: keepQuery('/ranking') },
    { path: '/shops', component: () => import('./views/ShopsView.vue'), meta: { title: '店铺工作台', roles: ['admin', 'shop_ops'] } },
    { path: '/identity', component: () => import('./views/IdentityView.vue'), meta: { title: '账号身份', roles: ['admin'] } },
    { path: '/proxies', component: () => import('./views/ProxyManagementView.vue'), meta: { title: 'IP 管理', roles: ['admin'] } },
    {
      path: '/products', component: () => import('./views/ProductsWorkspaceView.vue'), redirect: '/products/catalog',
      children: [
        { path: 'catalog', component: () => import('./views/ProductsView.vue'), meta: { title: '商品工作台' } },
        { path: 'config', component: () => import('./views/DataAssetsView.vue'), meta: { title: '商品配置' } }
      ]
    },
    {
      path: '/jobs', component: () => import('./views/JobsWorkspaceView.vue'), redirect: '/jobs/all',
      children: [
        { path: 'all', component: () => import('./views/AllJobsView.vue'), meta: { title: '作业中心' } },
        { path: 'tasks', component: () => import('./views/TasksView.vue'), meta: { title: '秒杀作业' } },
        { path: 'collections', component: () => import('./views/CollectionsView.vue'), meta: { title: '数据采集' } },
        { path: 'activity', component: () => import('./views/OperationsView.vue'), meta: { title: '活动记录' } }
      ]
    },
    { path: '/jobs/tasks/:id', component: () => import('./views/TaskDetailView.vue'), meta: { title: '作业详情' } },
    { path: '/jobs/automation/:id', component: () => import('./views/JobDetailView.vue'), meta: { title: '作业详情' } },
    {
      path: '/analytics', component: () => import('./views/AnalyticsWorkspaceView.vue'), redirect: '/analytics/overview',
      children: [
        { path: 'overview', component: () => import('./views/DataOverviewView.vue'), meta: { title: '经营概览' } },
        { path: 'realtime', component: () => import('./views/RealtimeDataView.vue'), meta: { title: '实时经营' } },
        { path: 'daily', component: () => import('./views/DailyDataView.vue'), meta: { title: '每日经营' } },
        { path: 'monthly', component: () => import('./views/MonthlyDataView.vue'), meta: { title: '月度经营' } }
      ]
    },
    {
      path: '/system', component: () => import('./views/SystemWorkspaceView.vue'), redirect: '/system/users', meta: { roles: ['admin'] },
      children: [
        { path: 'users', component: () => import('./views/UsersView.vue'), meta: { title: '用户与权限', roles: ['admin'] } },
        { path: 'teams', component: () => import('./views/TeamsView.vue'), meta: { title: '个人数据权限', roles: ['admin'] } },
        { path: 'licenses', component: () => import('./views/ToolboxLicensesView.vue'), meta: { title: '激活码管理', roles: ['admin'] } },
        { path: 'logs', component: () => import('./views/ActionLogsView.vue'), meta: { title: '日志中心', roles: ['admin'] } },
        { path: 'settings', component: () => import('./views/SettingsView.vue'), meta: { title: '运行设置', roles: ['admin'] } }
      ]
    },

    // V2 地址继续可用，书签和历史链接会无感迁移到 V3 工作台。
    { path: '/tasks', redirect: keepQuery('/jobs/tasks') },
    { path: '/tasks/:id', redirect: to => ({ path: `/jobs/tasks/${to.params.id}`, query: to.query }) },
    { path: '/data', redirect: keepQuery('/analytics/overview') },
    { path: '/data/realtime', redirect: keepQuery('/analytics/realtime') },
    { path: '/data/daily', redirect: keepQuery('/analytics/daily') },
    { path: '/data/monthly', redirect: keepQuery('/analytics/monthly') },
    { path: '/data/gmv', redirect: keepQuery('/ranking') },
    { path: '/data/assets', redirect: keepQuery('/products/config') },
    { path: '/data/collections', redirect: keepQuery('/jobs/collections') },
    { path: '/operations', redirect: keepQuery('/jobs/activity') },
    { path: '/users', redirect: keepQuery('/system/users') },
    { path: '/settings', redirect: keepQuery('/system/settings') },
    { path: '/:pathMatch(.*)*', redirect: '/dashboard' }
  ]
})

router.beforeEach(async to => {
  const auth = useAuthStore()
  if (!to.meta.public && !auth.loggedIn) return { path: '/login', query: { redirect: to.fullPath } }
  if (auth.loggedIn) {
    try { await auth.refreshUser() } catch { auth.clearSession(); return to.path === '/login' ? undefined : '/login' }
    if (auth.user?.must_change_password) return to.path === '/change-password' ? undefined : '/change-password'
  }
  if (to.path === '/change-password') return auth.loggedIn ? '/dashboard' : '/login'
  const home = auth.user?.role === 'shop_ops' ? '/shops' : '/dashboard'
  if (auth.user?.role === 'shop_ops' && !['/shops', '/products/catalog', '/login'].includes(to.path)) return home
  if (to.path === '/login' && auth.loggedIn) return home
  if (auth.user?.role === 'operator') {
    if (to.path === '/jobs/all') return '/jobs/tasks'
    if (!['/dashboard', '/products/catalog', '/group-products', '/group-members', '/jobs/tasks'].includes(to.path) && !/^\/jobs\/tasks\/\d+$/.test(to.path)) return home
  }
  const roles = to.matched.flatMap(record => (record.meta.roles || []) as Role[])
  if (roles.length && auth.user && !roles.includes(auth.user.role)) return '/dashboard'
})

router.afterEach((to, from, failure) => {
  if (!failure && !to.meta.public && to.fullPath !== from.fullPath) trackUi('page_view', String(to.meta.title || to.path))
})

export default router
